import { message } from 'antd';
import { useCallback, useMemo } from 'react';
import {
  createStoreNameConfig,
  getStoreNameConfig,
  StoreNameConfig,
  updateStoreNameConfig,
} from 'services/api/store';
import { toUpperCase } from 'utils/toLowerCase';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { atomWithDefault, loadable } from 'jotai/utils';

import { useMount } from 'ahooks';
import { tolgee } from 'services/i18n/tolgee';

const storeNameConfigRefreshState = atom(0);

const storeNameConfigState = atomWithDefault((get) => {
  get(storeNameConfigRefreshState);
  return getStoreNameConfig()
    .then((config) => {
      return config;
    })
    .catch((err) => {
      message.error(tolgee.t('department.获取店铺备注配置失败'));

      return Promise.reject(err);
    });
});

export const useStoreNameConfig = () => {
  const setStoreNameConfig = useSetAtom(storeNameConfigRefreshState);
  const storeNameConfigLoadable = useAtomValue(loadable(storeNameConfigState));
  const data =
    storeNameConfigLoadable.state === 'hasData' ? storeNameConfigLoadable.data : undefined;
  const run = useCallback(() => {
    setStoreNameConfig((n) => n + 1);
  }, [setStoreNameConfig]);

  useMount(() => {
    if (storeNameConfigLoadable.state === 'hasError') {
      run();
    }
  });
  const updateConfig = useCallback(
    (id: string, aliasFlag: boolean) => {
      updateStoreNameConfig(id, aliasFlag).then(run);
    },
    [run],
  );
  const createConfig = useCallback(
    (params: {
      platform: General.PlatformUpperCase;
      region: General.UpperCaseRegion;
      aliasFlag: boolean;
    }) => {
      createStoreNameConfig(params).then(run);
    },
    [run],
  );

  const configMap = useMemo(() => {
    return (data || []).reduce<
      Partial<Record<General.PlatformUpperCase, Partial<Record<General.UpperCaseRegion, boolean>>>>
    >((res, cur) => {
      const { platform, region, aliasFlag } = cur;

      const platformConfig = res[platform];

      if (!platformConfig) {
        res[platform] = {
          [region]: aliasFlag,
        };

        return res;
      }

      platformConfig[region] = aliasFlag;

      return res;
    }, {});
  }, [data]);

  return {
    updateConfig,
    createConfig,
    config: data,
    configMap,
    refresh: run,
  };
};
export const isStoreAliasFirst = (
  config?: StoreNameConfig[],
  platform?: General.PlatformTunnel | General.PlatformUpperCaseTunnel,
  region?: General.UpperCaseRegion | General.Region,
) => {
  if (!platform || !region || !config) {
    return false;
  }

  const target = config.find(
    (x) => x.platform === toUpperCase(platform) && x.region === toUpperCase(region),
  );
  return !!target?.aliasFlag;
};

export const useIsStoreAliasFirst = (
  platform?: General.PlatformTunnel | General.PlatformUpperCaseTunnel,
  region?: General.UpperCaseRegion | General.Region,
): boolean => {
  const { config } = useStoreNameConfig();
  return isStoreAliasFirst(config, platform, region);
};
